import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { mainSiteUrl } from "@/mocks/categories";

const siteUrl = "https://sugargoosheet.casa";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Sugargoo Spreadsheet for Dropshipping: Scale Your Store With Organized Product Research",
  description: "How dropshippers can leverage the Sugargoo Spreadsheet to find products faster, compare suppliers smarter, and build a profitable dropshipping business from China.",
  image: "https://readdy.ai/api/search-image?query=Modern%20ecommerce%20dropshipping%20workspace%20with%20laptop%20showing%20online%20store%20dashboard%20surrounded%20by%20product%20samples%20packaging%20materials%20and%20shipping%20boxes%20on%20minimal%20desk%20natural%20lighting%20professional%20home%20office%20aesthetic&width=1400&height=700&seq=dropshipping-guide-spreadsheet&orientation=landscape",
  datePublished: "2026-08-09",
  dateModified: "2026-08-09",
  author: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  publisher: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  inLanguage: "en",
  isPartOf: { "@type": "WebSite", name: "Sugargoo Spreadsheet", url: siteUrl },
};

export default function DropshippingGuide() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">Sugargoo Spreadsheet for Dropshipping</span>
            </nav>

            <header className="mb-10">
              <div className="inline-block text-xs font-medium text-accent-500 bg-accent-50 px-3 py-1 rounded-full mb-4">Dropshipping</div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                Sugargoo Spreadsheet for Dropshipping: Scale Your Store With Organized Product Research
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-2xl">
                How dropshippers can leverage the Sugargoo Spreadsheet to find products faster, compare suppliers smarter, and build a profitable dropshipping business from China.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-400">
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i> Updated: August 2026</span>
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i> 7 min read</span>
              </div>
            </header>

            <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img src="https://readdy.ai/api/search-image?query=Modern%20ecommerce%20dropshipping%20workspace%20with%20laptop%20showing%20online%20store%20dashboard%20surrounded%20by%20product%20samples%20packaging%20materials%20and%20shipping%20boxes%20on%20minimal%20desk%20natural%20lighting%20professional%20home%20office%20aesthetic&width=1400&height=700&seq=dropshipping-guide-spreadsheet&orientation=landscape" alt="Sugargoo Spreadsheet for Dropshipping" className="w-full h-full object-cover" />
            </div>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Why Dropshippers Need the Sugargoo Spreadsheet</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Dropshipping is a game of speed and margins. The faster you can identify trending products and the better your supplier pricing, the more profitable your store becomes. The <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Sugargoo Spreadsheet</Link> gives dropshippers a significant advantage by providing pre-researched product data that would take weeks to compile independently.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Instead of spending 80% of your time on product research and only 20% on marketing and sales, a structured spreadsheet flips that ratio — you spend most of your time growing your business while the research foundation is already laid out for you.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Dropshipping Workflow With the Spreadsheet</h2>
              <div className="space-y-3">
                {[
                  "Identify trending niches by sorting products by recent popularity indicators in the spreadsheet.",
                  "Select 5-10 products per niche and create test listings on your store (Shopify, WooCommerce, etc.).",
                  "Run small-budget Facebook or TikTok ads to test audience response to each product.",
                  "Double down on the products that generate sales — increase ad spend and optimize listings.",
                  "Use the spreadsheet supplier data to negotiate better pricing as your order volume grows.",
                  "Regularly refresh your product lineup with new spreadsheet finds to keep your store competitive.",
                ].map((step, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg">
                    <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-50 mt-0.5">
                      <span className="text-xs font-bold text-primary-500">{i + 1}</span>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Key Metrics for Dropshippers</h2>
              <div className="overflow-x-auto rounded-xl border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-900">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Metric</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Target</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">How Spreadsheet Helps</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Product Cost","Under $15","Sort by price to find affordable items"],
                      ["Shipping Weight","Under 500g","Filter by weight for cost-effective shipping"],
                      ["Profit Margin","3x minimum","Calculate using spreadsheet price data"],
                      ["Supplier Rating","4+ stars","Use supplier rating column to filter"],
                      ["Processing Time","Under 5 days","Check supplier notes for speed"],
                    ].map((row, i) => (
                      <tr key={i} className={`border-t border-background-200/70 ${i % 2 === 0 ? "bg-background-50" : "bg-background-100"}`}>
                        <td className="px-4 py-3 text-xs font-medium text-foreground-900">{row[0]}</td>
                        <td className="px-4 py-3 text-xs text-primary-600 font-medium">{row[1]}</td>
                        <td className="px-4 py-3 text-xs text-foreground-600">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="bg-background-900 rounded-2xl p-8 md:p-10 text-center mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">Scale Your Dropshipping Business</h2>
              <p className="text-sm text-background-400 max-w-md mx-auto mb-6">Get the Sugargoo Spreadsheet and give your dropshipping store the product research advantage it needs.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-900 bg-primary-400 hover:bg-primary-300 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer">Get Sugargoo Spreadsheet</a>
                <Link to="/" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-lg transition-all duration-200 whitespace-nowrap">Back to Homepage</Link>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Related Resources</h3>
              <ul className="space-y-1.5">
                <li><Link to="/blog/how-to-find-winning-products" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">How to Find Winning Products With Sugargoo Spreadsheet</Link></li>
                <li><Link to="/blog/sugargoo-shipping-cost-guide" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Shipping Cost Guide</Link></li>
                <li><Link to="/" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Spreadsheet Homepage</Link></li>
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}